import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { AuthLayout } from "@/components/ui/auth-layout";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Panel } from "@/components/ui/panel";
import { Spinner } from "@/components/ui/spinner";
import { Muted, Text } from "@/components/ui/text";
import { useAuth } from "@/contexts/auth-context";
import { isAuthenticationError } from "@/lib/api-errors";
import { backendAuth } from "@/lib/auth/backend-auth";
import type { BackendLoginOptions } from "@/lib/types/auth";

type LoginAction = "sign-in";

const primaryActionClass =
  "w-full rounded-full bg-[color:var(--rf-color-accent)] text-emerald-950 hover:bg-[color:var(--rf-color-primary-strong)] focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-2 focus-visible:ring-offset-white text-base font-semibold py-3 transition-colors";

interface ResolvePostLoginRedirectPathOptions {
  userId: string | null;
  isLoading: boolean;
  authError: Error | null;
  searchParams: URLSearchParams;
}

const inviteErrorMessages = {
  already_used: "This invite link was already used. Sign in to continue.",
  expired: "This invite link has expired. Ask your admin for a new invite.",
  revoked: "This invite link was revoked. Ask your admin for a new invite.",
  not_found: "This invite link is no longer valid. Ask your admin for a new invite.",
  invalid_token: "This invite link is invalid. Ask your admin for a new invite.",
  email_mismatch:
    "This invite was sent to a different email address. Sign in with the invited email or ask your admin for a new invite.",
  email_unverified:
    "This invite requires a verified email address. Verify your email and try again.",
  auth_error: "An authentication error occurred. Please try signing in again.",
} as const;

function resolveReturnToQueryParam(searchParams: URLSearchParams): string | null {
  const snakeCaseReturnTo = searchParams.get("return_to");
  if (snakeCaseReturnTo && snakeCaseReturnTo.trim()) {
    return snakeCaseReturnTo;
  }

  const camelCaseReturnTo = searchParams.get("returnTo");
  if (camelCaseReturnTo && camelCaseReturnTo.trim()) {
    return camelCaseReturnTo;
  }

  return null;
}

export function resolvePostLoginRedirectPath({
  userId,
  isLoading,
  authError,
  searchParams,
}: ResolvePostLoginRedirectPathOptions): string | null {
  if (isLoading || !userId) {
    return null;
  }

  if (authError != null && isAuthenticationError(authError)) {
    return null;
  }

  const returnTo = resolveReturnToQueryParam(searchParams) ?? "/analytics";
  return returnTo;
}

export function resolveInviteErrorMessage(searchParams: URLSearchParams): string | null {
  if (searchParams.get("reason") !== "invite_error") {
    return null;
  }
  const genericInviteErrorMessage =
    "This invite link is no longer valid. Ask your admin for a new invite.";
  const inviteError = searchParams.get("invite_error");
  if (inviteError == null || inviteError.trim() === "") {
    return genericInviteErrorMessage;
  }
  const normalized = inviteError.trim();
  if (normalized in inviteErrorMessages) {
    return inviteErrorMessages[normalized as keyof typeof inviteErrorMessages];
  }
  return genericInviteErrorMessage;
}

export default function LoginRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId, isLoading, error: authError } = useAuth();

  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState<LoginAction | null>(null);

  const isLoginPending = pendingAction !== null;

  const resolveReturnTo = (override?: string | null): string | undefined => {
    if (override && override.trim()) {
      return override;
    }

    const param = resolveReturnToQueryParam(searchParams);
    return param ?? undefined;
  };

  const buildLoginOptions = (overrideReturnTo?: string | null): BackendLoginOptions => {
    const returnTo = resolveReturnTo(overrideReturnTo);
    return returnTo ? { returnTo } : {};
  };

  const startLogin = (overrideReturnTo?: string | null): void => {
    const options = buildLoginOptions(overrideReturnTo);
    backendAuth.login(options);
  };

  // Redirect if already authenticated
  useEffect(() => {
    const postLoginRedirectPath = resolvePostLoginRedirectPath({
      userId,
      isLoading,
      authError,
      searchParams,
    });
    if (postLoginRedirectPath) {
      void navigate(postLoginRedirectPath, { replace: true });
    }
  }, [authError, userId, isLoading, navigate, searchParams]);

  const handleSignIn = (): void => {
    setPendingAction("sign-in");
    setError("");

    try {
      startLogin();
    } catch (err) {
      console.error("Login flow failed:", err);
      setError("We were unable to start the login flow. Please try again or contact support.");
      setPendingAction(null);
    }
  };

  const inviteErrorMessage = resolveInviteErrorMessage(searchParams);
  const sessionExpired =
    inviteErrorMessage == null && searchParams.get("reason") === "session_expired";

  if (isLoading) {
    return (
      <AuthLayout>
        <Panel className="flex items-center justify-center gap-3 bg-white/85 text-foreground">
          <Spinner
            size="md"
            label="Checking your session"
            className="text-[color:var(--rf-color-primary)]"
          />
          <Text tone="muted">Checking your session…</Text>
        </Panel>
      </AuthLayout>
    );
  }

  // Don't show login if already authenticated
  if (userId) {
    return null;
  }

  return (
    <AuthLayout>
      <Panel className="space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <img src="/logo-20260129.svg" alt="Rollforward" className="h-12 w-auto" />
          <Heading size="lg">Sign in to Rollforward</Heading>
          <Muted size="sm">Use your credentials to access your workspace.</Muted>
        </div>

        {inviteErrorMessage && (
          <Alert variant="destructive">
            <AlertDescription>{inviteErrorMessage}</AlertDescription>
          </Alert>
        )}

        {sessionExpired && (
          <Alert variant="destructive">
            <AlertDescription>Your session expired. Please log in again.</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button className={primaryActionClass} onClick={handleSignIn} disabled={isLoginPending}>
          {pendingAction === "sign-in" ? "Starting..." : "Sign in"}
        </Button>
      </Panel>
    </AuthLayout>
  );
}
